import {
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Button,
    Center,
    Flex,
    Grid,
    GridItem,
    HStack,
    Heading,
    Icon,
    Link,
    Menu,
    MenuButton,
    MenuDivider,
    MenuItem,
    MenuItemOption,
    MenuList,
    MenuOptionGroup,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    Spacer,
    Stack,
    Tag,
    Text,
    VStack,
    Wrap,
    WrapItem,
    useColorModeValue,
} from "@chakra-ui/react";
import AvatarImport from "boring-avatars";
import { CZ, GB, SK } from "country-flag-icons/react/3x2";
import { useTranslation } from "react-i18next";
import moment from "moment/min/moment-with-locales";
import { Fragment, useState } from "react";
import { FaItchIo } from "react-icons/fa";
import {
    RiArrowDropDownLine,
    RiArrowLeftSLine,
    RiArrowRightSLine,
    RiFacebookCircleFill,
    RiGlobalLine,
    RiInstagramFill,
    RiSteamFill,
    RiTwitterXLine,
} from "react-icons/ri";
import { useLoaderData, type LoaderFunctionArgs } from "react-router";

// boring-avatars ships transpiled CJS; when Node loads it externally during
// SSR the default import is the whole exports object instead of the component.
const Avatar = ((AvatarImport as any).default ?? AvatarImport) as typeof AvatarImport;
import { GameMedia, getApiV1GamesById, getApiV1ReleasesById } from "../../client";
import { MEDIA_ASPECT } from "../../constants";
import { apiData } from "../../utils/api";
import { capitalize } from "../../utils/capitalize";
import { defaultStreamUrl } from "../../utils/getStreamUrl";
import { getPrefered } from "../../utils/preferedLanguage";
import { ContentPreview, ContentViewer } from "../media/ContentViewer";
import { PivoImage } from "../media/PivoImage";
import { ChakraMarkdown } from "../utils/ChakraMarkdown";
import { PageTitle } from "../utils/PageTitle";

export { RouteErrorBoundary as ErrorBoundary } from "../utils/RouteErrorBoundary";

const iconMap = {
    "steampowered.com": <RiSteamFill size={20} />,
    "instagram.com": <RiInstagramFill size={20} />,
    "facebook.com": <RiFacebookCircleFill size={20} />,
    "itch.io": <FaItchIo size={20} />,
    "x.com": <RiTwitterXLine size={20} />,
};

function renderIcon(url: string) {
    const lowercasedUrl = url.toLowerCase();
    for (const [key, icon] of Object.entries(iconMap)) {
        if (lowercasedUrl.includes(key)) {
            return icon;
        }
    }
    return <RiGlobalLine size={20} />;
}

export async function loader({ params }: LoaderFunctionArgs) {
    const { gameId, releaseId } = params;

    if (!gameId || !releaseId) {
        throw new Response("Game or version not found!", { status: 404 });
    }

    const [release, game] = await Promise.all([
        apiData(getApiV1ReleasesById({ path: { id: releaseId } })),
        apiData(getApiV1GamesById({ path: { id: gameId } })),
    ]);

    return { release, game };
}

export function Release() {
    const { t } = useTranslation();
    const { release, game } = useLoaderData<typeof loader>();

    const [selectedMedia, setSelectedMedia] = useState<GameMedia | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const mediaShadow = useColorModeValue(
        "2px 2px 10px rgba(128, 128, 128, 0.7)",
        "2px 2px 10px rgba(44, 48, 68, 0.7)",
    );
    const subtitleColor = useColorModeValue("gray.400", "gray.500");
    const labelColor = useColorModeValue("gray.500", "gray.400");
    const contentColor = useColorModeValue("gray.900", "gray.100");
    const tagBorderColor = useColorModeValue("gray.300", "gray.700");
    const hoverBg = useColorModeValue("gray.200", "gray.800");
    const downloadBg = useColorModeValue("brand.500", "brand.400");
    const downloadHoverBg = useColorModeValue("brand.400", "brand.500");

    const openModal = (media: GameMedia) => {
        setSelectedMedia(media);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setSelectedMedia(null);
        setIsModalOpen(false);
    };

    const getLanguageFlag = (language: string) => {
        switch (language) {
            case "cs":
                return <Icon as={CZ} boxSize={6} />;
            case "en":
                return <Icon as={GB} boxSize={6} />;
            case "sk":
                return <Icon as={SK} boxSize={6} />;
            default:
                return <></>;
        }
    };

    // Correct way to access the second media item, if it exists
    const primaryMedia = release.media?.filter((media) => media.mediaType.startsWith("image"))[0];
    const primaryMediaId = primaryMedia?.attachmentId;
    const primaryMediaThumbHash = primaryMedia?.thumbHash;

    const downloadLikeLinks = release.links?.filter((link) =>
        ["steampowered.com", "itch.io"].some((match) => link.url.includes(match)),
    );

    return (
        <Box>
            <Box position="relative" w="100%" minH={10}>
                {primaryMediaId && (
                    <Box sx={{boxShadow: mediaShadow}}>
                        <PivoImage
                            id={primaryMediaId}
                            thumbHash={primaryMediaThumbHash}
                            sizes="512px"
                            overflowY="hidden"
                            imageProps={{
                                alt: "",
                                w: "100%",
                                h: { base: 36, sm: 36, md: 56, lg: 72 },
                                filter: "blur(0.5rem)",
                                objectFit: "cover",
                                objectPosition: "center center",
                            }}
                        />
                        <Box
                            position="absolute"
                            top={0}
                            left={0}
                            w="100%"
                            h="100%"
                            backgroundColor="rgba(0, 0, 0, 0.4)"
                        />
                    </Box>
                )}

                <Breadcrumb position="absolute" top={7} left={10} color={primaryMediaId ? "white" : undefined}>
                    <BreadcrumbItem>
                        <BreadcrumbLink href="/">Garrigue Games</BreadcrumbLink>
                    </BreadcrumbItem>

                    <BreadcrumbItem>
                        <BreadcrumbLink href="/games">{t("menuButtons.games")}</BreadcrumbLink>
                    </BreadcrumbItem>

                    <BreadcrumbItem fontWeight={"semibold"} isCurrentPage>
                        <BreadcrumbLink href={"games/" + release.id}>{getPrefered(game.name)}</BreadcrumbLink>
                    </BreadcrumbItem>
                </Breadcrumb>
            </Box>

            <VStack maxW={1536} p={10} alignItems={"left"}>
                <Stack direction={{ base: "column", md: "row" }} align={"baseline"} gap={0}>
                    <VStack alignItems={"left"} gap={0}>
                        <PageTitle>{getPrefered(game.name)}</PageTitle>
                        {game.releases.length > 1 && (
                            <Text
                                fontSize="sm"
                                fontFamily="body"
                                fontWeight="regular"
                                fontStyle="italic"
                                color={subtitleColor}
                            >
                                {t("games.version")} {moment(release.releasedOn).calendar()}
                            </Text>
                        )}

                        <Wrap spacing="10px" marginBottom={5} mt={4}>
                            {release.tags?.map((tag) => (
                                <WrapItem key={tag!.id ?? getPrefered(tag!.name)}>
                                    <Center>
                                        <Tag
                                            size="md"
                                            fontSize="xs"
                                            variant={"transparent"}
                                            border={"1px"}
                                            backgroundColor={"transparent"}
                                            borderColor={tagBorderColor}
                                        >
                                            <Text fontSize="xs">{getPrefered(tag.name)}</Text>
                                        </Tag>
                                    </Center>
                                </WrapItem>
                            ))}
                        </Wrap>
                    </VStack>
                    <Spacer />
                    <Stack direction={{ base: "row", md: "column" }} paddingBottom={3} wrap={"wrap"}>
                        {game.releases.length > 1 && (
                            <Menu>
                                <MenuButton
                                    w={40}
                                    as={Button}
                                    size={"sm"}
                                    variant="transparent"
                                    fontSize="sm"
                                    border={"1px"}
                                    backgroundColor={"transparent"}
                                    borderColor={tagBorderColor}
                                    rightIcon={<RiArrowDropDownLine size={20} />}
                                    iconSpacing="2px"
                                    paddingX={3}
                                    _hover={{
                                        bg: hoverBg,
                                    }}
                                    _active={{
                                        bg: hoverBg,
                                    }}
                                >
                                    <Text isTruncated>
                                        {t("games.version")} {moment(release.releasedOn).calendar()}
                                    </Text>
                                </MenuButton>
                                <MenuList>
                                    <MenuOptionGroup defaultValue={release.id} type="radio">
                                        {game.releases.map((release) => (
                                            <MenuItemOption
                                                as="a"
                                                href={release.id}
                                                key={release.id}
                                                value={release.id}
                                            >
                                                {t("games.version")} {release.id}
                                            </MenuItemOption>
                                        ))}
                                    </MenuOptionGroup>
                                </MenuList>
                            </Menu>
                        )}
                        {release.downloads.length + downloadLikeLinks.length > 0 && (
                            <Menu>
                                <MenuButton
                                    w={40}
                                    as={Button}
                                    size={"sm"}
                                    variant="solid"
                                    fontSize="sm"
                                    textColor={"gray.50"}
                                    fontWeight={"semibold"}
                                    backgroundColor={downloadBg}
                                    rightIcon={<RiArrowDropDownLine size={22} />}
                                    _hover={{
                                        bg: downloadHoverBg,
                                    }}
                                    _active={{
                                        bg: downloadHoverBg,
                                    }}
                                >
                                    {t("games.download")}
                                </MenuButton>

                                <MenuList>
                                    {downloadLikeLinks.map((link) => (
                                        <Link href={link.url} target="_blank" rel="noopener noreferrer" key={link.url}>
                                            <MenuItem>
                                                {t("games.playOn")} {getPrefered(link.label)}
                                            </MenuItem>
                                        </Link>
                                    ))}
                                    {downloadLikeLinks.length > 0 && release.downloads.length > 0 && <MenuDivider />}
                                    {release.downloads.map((download) => (
                                        <Link
                                            key={download.attachmentId}
                                            href={defaultStreamUrl(download.attachmentId)}
                                            download
                                        >
                                            <MenuItem>
                                                {download.tags
                                                    .map((tag) => capitalize(getPrefered(tag.name)))
                                                    .join(", ").length > 0 ? (
                                                    <>
                                                        {t("games.downloadFor")}{" "}
                                                        {download.tags
                                                            .map((tag) => capitalize(getPrefered(tag.name)))
                                                            .join(", ")}
                                                    </>
                                                ) : (
                                                    t("games.download")
                                                )}
                                            </MenuItem>
                                        </Link>
                                    ))}
                                </MenuList>
                            </Menu>
                        )}
                    </Stack>
                </Stack>

                {release.authors?.length > 0 && (
                    <>
                        <Heading fontSize="xl" fontWeight={"medium"}>
                            {t("games.authors")}
                        </Heading>
                        <Box marginBottom={5} fontSize="md">
                            <Wrap spacingX={10} spacingY={5} marginTop={2}>
                                {release.authors?.map((author) => (
                                    <WrapItem
                                        // Authors come without a stable id; fall back to the name
                                        key={author.id ?? author.name}
                                        width={{ base: "100%", md: "40%", lg: "30%", xl: "20%" }}
                                        maxWidth={{ base: "100%", md: "40%", lg: "30%", xl: "20%" }}
                                    >
                                        <HStack
                                            spacing={0}
                                            align={author.roles.join(", ").trim().length > 0 ? "start" : "center"}
                                        >
                                            <Center flexShrink={0} width="40px" height="40px" position="relative">
                                                <Avatar
                                                    size={40}
                                                    name={author.name}
                                                    variant="marble"
                                                    colors={["#CA68C8", "#EE81A7", "#FC944A", "#FFD770", "#C0ECCD"]}
                                                />
                                                <Center position="absolute" inset="0" color="white">
                                                    <Text
                                                        textAlign="center"
                                                        fontSize={"md"}
                                                        fontWeight="bold"
                                                        letterSpacing={1}
                                                    >
                                                        {author.name
                                                            .split(" ")
                                                            .map((word) => (word[0] || "").toUpperCase())
                                                            .slice(0, 2)
                                                            .join("")}
                                                    </Text>
                                                </Center>
                                            </Center>
                                            <VStack spacing={0} align="start">
                                                <Text marginLeft={3} fontSize={"sm"}>
                                                    {author.name}
                                                </Text>
                                                <Text marginLeft={3} fontSize={"sm"} opacity={0.5}>
                                                    {author.roles.join(", ")}
                                                </Text>
                                            </VStack>
                                        </HStack>
                                    </WrapItem>
                                ))}
                            </Wrap>
                        </Box>
                    </>
                )}
                {getPrefered(release.description).trim().length > 0 && (
                    <>
                        <Heading fontSize="xl" fontWeight={"medium"}>
                            {t("games.about")}
                        </Heading>
                        <Box fontSize="sm" maxW={1536} marginBottom={5}>
                            <ChakraMarkdown>{getPrefered(release.description)}</ChakraMarkdown>
                        </Box>
                    </>
                )}

                <Grid
                    marginBottom={5}
                    templateColumns={{
                        base: "1",
                        md: "repeat(2, minmax(0, 1fr))",
                        lg: "repeat(3, minmax(0, 1fr))",
                        xl: "repeat(4, minmax(0, 1fr))",
                    }}
                    gap={0}
                >
                    {release.media?.map((media, index) => {
                        const isHighlighted = index === 0;

                        return (
                            <GridItem
                                aspectRatio={MEDIA_ASPECT}
                                key={media.attachmentId}
                                rowSpan={{ base: 1, md: isHighlighted ? 2 : 1 }}
                                colSpan={{ base: 1, md: isHighlighted ? 2 : 1 }}
                                pr={4}
                                pb={4}
                            >
                                <Box
                                    w="100%"
                                    h="100%"
                                    borderRadius="md"
                                    overflow={"hidden"}
                                    sx={{
                                        boxShadow: mediaShadow,
                                    }}
                                    cursor={"pointer"}
                                >
                                    <ContentPreview media={media} onClick={() => openModal(media)} />
                                </Box>
                            </GridItem>
                        );
                    })}
                </Grid>

                <Heading fontSize="xl" fontWeight={"medium"}>
                    {t("games.gamedetails")}
                </Heading>
                <Grid
                    w="auto"
                    autoRows="auto"
                    templateColumns={{ base: "1fr 1fr", md: "1fr 7fr" }}
                    gap={2}
                    marginBottom={5}
                >
                    {/* <GridItem>
                    <Text
                        fontSize="sm"
                        as="span"
                        color={labelColor}
                        fontWeight="bold"
                    >
                        {t('games.genre') + ':'}
                    </Text>
                </GridItem> */}
                    {/* <GridItem>
                    {release.genre?.map((genre, index) => (
                        <Text key={genre!.iv} fontSize="sm" as="span">
                            {i18next.language === 'cs' ? genre!.cs : genre!.iv}
                            {index < release.genre.length - 1 && ' — '}
                        </Text>
                    ))}
                </GridItem> */}

                    {/* {game.type!.iv == 'Video Game' && (
<>
    <GridItem>
        <Text
            fontSize="sm"
            as="span"
            color={labelColor}
            fontWeight="bold"
        >
            {t('games.workson') + ':'}
        </Text>
    </GridItem>
    <GridItem>
        <Text fontSize="sm" as="span">
            {release.downloads![0].platform?.iv}
        </Text>
    </GridItem>
</>
)} */}

                    {release.releasedOn && (
                        <>
                            <GridItem>
                                <Text fontSize="sm" as="span" color={labelColor} fontWeight="bold">
                                    {t("games.releasedate") + ":"}
                                </Text>
                            </GridItem>
                            <GridItem>
                                <Text fontSize="sm" as="span">
                                    {moment(release.releasedOn).calendar()}
                                </Text>
                            </GridItem>
                        </>
                    )}

                    {/* {game.type!.iv == 'Video Game' && (
<>
    <GridItem>
        <Text
            fontSize="sm"
            as="span"
            color={labelColor}
            fontWeight="bold"
        >
            {t('games.size') + ':'}
        </Text>
    </GridItem>
    <GridItem>
        <Text fontSize="sm" as="span">
            {release.downloads![0].size + ' B'}
        </Text>
    </GridItem>
</>
)} */}

                    {release.supportedLanguages.length > 0 && (
                        <>
                            <GridItem>
                                <Text fontSize="sm" as="span" color={labelColor} fontWeight="bold">
                                    {t("games.language") + ":"}
                                </Text>
                            </GridItem>
                            <GridItem>
                                <HStack spacing={2}>
                                    {release.supportedLanguages.map((language) => (
                                        <Fragment key={language}>
                                            {getLanguageFlag(language)}
                                            <Text fontSize="sm" as="span" mr={6}>
                                                {language.toUpperCase()}
                                            </Text>
                                        </Fragment>
                                    ))}
                                </HStack>
                            </GridItem>
                        </>
                    )}

                    {/* <GridItem>
                    <Text
                        fontSize="sm"
                        color={labelColor}
                        fontWeight="bold"
                        as="span"
                    >
                        {t('games.license') + ':'}{' '}
                    </Text>
                </GridItem>
                <GridItem>
                    <Text fontSize="sm" as="span">
                        {i18next.language === 'cs'
                            ? release.license!.name!.cs
                            : release.license!.name!.iv}
                    </Text>
                </GridItem> */}
                </Grid>
                {/* <Heading fontSize="lg" fontWeight={'medium'}>
                {t('games.extras')}
            </Heading>
            <Button
                variant="link"
                size="sm"
                fontSize="sm"
                fontWeight={'medium'}
                rightIcon={<RiExternalLinkLine />}
                color={contentColor}
                width={'fit-content'}
            >
                Devlogs
            </Button>
            <Button
                variant="link"
                size="sm"
                fontSize="sm"
                fontWeight={'medium'}
                rightIcon={<RiExternalLinkLine />}
                color={contentColor}
                width={'fit-content'}
                marginBottom={5}
            >
                Game Design Documents
            </Button> */}

                {release.links?.length > 0 && (
                    <>
                        <Heading fontSize="lg" fontWeight={"medium"}>
                            {t("games.links")}
                        </Heading>

                        {release.links?.map((link) => (
                            <Link key={link.url} href={link.url} target="_blank" rel="noopener noreferrer">
                                <Button
                                    key={link.url}
                                    variant="link"
                                    size="sm"
                                    fontSize="sm"
                                    fontWeight={"medium"}
                                    color={contentColor}
                                    width={"fit-content"}
                                    leftIcon={renderIcon(link.url)}
                                >
                                    {getPrefered(link.label)}
                                </Button>
                            </Link>
                        ))}
                    </>
                )}
            </VStack>

            <Modal isOpen={isModalOpen} onClose={closeModal} size={"full"} isCentered>
                <ModalOverlay />
                <ModalContent
                    m={0}
                    borderRadius="none"
                    bg="none"
                    onKeyDown={(e) => {
                        const index = release.media.findIndex((m) => m.attachmentId === selectedMedia?.attachmentId);
                        if (e.key === "Escape") {
                            closeModal();
                        } else if (e.key === "ArrowRight") {
                            e.stopPropagation();
                            e.preventDefault();
                            const nextIndex = (index + 1) % release.media.length;
                            setSelectedMedia(release.media[nextIndex]);
                        } else if (e.key === "ArrowLeft") {
                            e.stopPropagation();
                            e.preventDefault();
                            const prevIndex = (index - 1 + release.media.length) % release.media.length;
                            setSelectedMedia(release.media[prevIndex]);
                        }
                    }}
                >
                    <ModalCloseButton size="lg" zIndex="2" position="absolute" top="4" right="4" color="white" />
                    <ModalBody p={0} overflow="hidden" position="relative" onClick={closeModal}>
                        <Flex
                            position="absolute"
                            top={0}
                            right={0}
                            bottom={0}
                            w="24"
                            cursor="pointer"
                            flexDir="column"
                            alignItems="end"
                            justifyContent="center"
                            zIndex={1}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                const index = release.media.findIndex(
                                    (m) => m.attachmentId === selectedMedia?.attachmentId,
                                );
                                const nextIndex = (index + 1) % release.media.length;
                                setSelectedMedia(release.media[nextIndex]);
                            }}
                        >
                            <Icon as={RiArrowRightSLine} fontSize="3em" color="white" />
                        </Flex>
                        <Flex
                            position="absolute"
                            top={0}
                            left={0}
                            bottom={0}
                            w="24"
                            cursor="pointer"
                            flexDir="column"
                            alignItems="start"
                            justifyContent="center"
                            zIndex={1}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                const index = release.media.findIndex(
                                    (m) => m.attachmentId === selectedMedia?.attachmentId,
                                );
                                const prevIndex = (index - 1 + release.media.length) % release.media.length;
                                setSelectedMedia(release.media[prevIndex]);
                            }}
                        >
                            <Icon as={RiArrowLeftSLine} fontSize="3em" color="white" />
                        </Flex>
                        <Box position="absolute" inset={{ base: 0, md: 10 }}>
                            {selectedMedia && <ContentViewer media={selectedMedia} />}
                        </Box>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
}

export default Release;
